import { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';

import styles from './EmployerProfile.module.css';
import BlockLoader from '../../../components/UI/Loaders/BlockLoader';
import ProfileSideBar from '../../../components/UI/Common/ProfileSideBar/ProfileSideBar';
import AboutMeSection from '../../../components/UI/Common/AboutMeSection/AboutMeSection';
import CompleteProfile from '../../../components/UI/Common/CompleteProfile/CompleteProfile';
import CompanyInformation from '../../../components/UI/Common/CompanyInformation/CompanyInformation';
import EmployerContactDetail from '../../../components/UI/Common/EmployerContactDetail/EmployerContactDetail';
import EmployerPersonalInformation from '../../../components/UI/Common/EmployerPersonalInformation/EmployerPersonalInformation';
import EmployerProfileJobseekerView from '../ProfileJobseekerView/ProfileJobseekerView';
import EmployerPersonalInformationIndividual from '../../../components/UI/Common/EmployerPersonalInformationIndividual/EmployerPersonalInformationIndividual';
import { getUser } from '../../../services/userService';
import { useSelector } from 'react-redux';
import {
    calculateEmployerIndividualProfileCompletion,
    calculateEmployerProfileCompletion
} from '../../../components/UI/Common/CalculateCompleteProfile/CalculateCompleteProfile';
import {
    Contact,
    Profile,
    WorkHistory
} from '../../../assets/images/icons';
import Account from '../../../components/UI/Common/Account/Account';
import NotFound from '../../../components/UI/Common/NotFound/NotFound';

function EmployerProfile() {
    const { t } = useTranslation();
    const { slug } = useParams();
    const [activeTab, setActiveTab] = useState(0);
    const [employer, setEmployer] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [completionPercentage, setCompletionPercentage] = useState(0);
    const [profileNotFound, setProfileNotFound] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const location = useLocation();

    useEffect(() => {
        const MAX_RETRIES = 1000;
        let retryCount = 0;
        setIsLoading(true);
        setProfileNotFound(false);

        const fetchJobs = (slug) => {
            getUser('employer', slug)
                .then((response) => {
                    setEmployer(response);
                    setIsLoading(false);
                })
                .catch((error) => {
                    if (error.error === 'out of shared memory' && retryCount < MAX_RETRIES) {
                        retryCount++;
                        fetchJobs(slug);
                    } else {
                        console.error('Failed to fetch jobs after retries:', error);
                        setIsLoading(false);
                        setProfileNotFound(true);
                    }
                });
        };
        if (slug) {
            fetchJobs(slug);
        } else if (location.pathname.includes('/profile')) {
            fetchJobs(user.user.slug)
        }
    }, [slug, location]);

    useEffect(() => {
        const selectedProfileTab = localStorage.getItem('selectedProfileTab');
        setActiveTab(Number(selectedProfileTab));
    }, []);

    const [tabs, setTabs] = useState([
        { name: 'personalInformation', icon: Profile },
        { name: 'contactInformation', icon: Contact },
        { name: 'companyInformation', icon: WorkHistory },
        { name: 'account', icon: WorkHistory },
    ])
    const user = useSelector(state => state.user);
    useEffect(() => {
        if (employer && user.user) {
            if (employer.employer_status !== 'business') {
                if (tabs[2].name == 'companyInformation') tabs.splice(2, 1)
                setTabs(tabs)
                const completion = calculateEmployerIndividualProfileCompletion(user.user);
                setCompletionPercentage(completion);
            } else {
                const completion = calculateEmployerProfileCompletion(user.user);
                setCompletionPercentage(completion);
            }
        }
    }, [employer]);


    // Function to get the active tab based on the URL path
    const getActiveTabFromPath = (path) => {
        const commonTabs = [
            '/profile/personal-information',
            '/profile/contact-information'
        ];

        const tabs = user?.user?.employer_status === 'business'
            ? [...commonTabs, '/profile/company-information', '/profile/account']
            : [...commonTabs, '/profile/account'];
        return tabs.findIndex(tab => path.includes(tab)) || 0;
    };
    useEffect(() => {
        const path = location.pathname;
        if(user && user.user){
            const activeTabIndex = getActiveTabFromPath(path);
            setActiveTab(activeTabIndex !== -1 ? activeTabIndex : 0);
        }else{
            setActiveTab(0);
        }
    }, [location.pathname,user]);
    return (
        <>
            {profileNotFound ? (
                <NotFound
                    title={t('not_found.title')}
                    heading={t('not_found.profile_not_found')}
                    message={t('not_found.profile_message')}
                    showHomeButton={false}
                />
            ) : (
                user?.user?.slug === slug && user?.user?.account_type === 'employer' || location.pathname.includes('/profile') ? (
                    <div style={{ position: 'relative' }}>
                        {!isLoading && employer ? (
                            <>
                                <Helmet>
                                    <title>FindCV - {employer.company_name || `${employer.first_name} ${employer.last_name}`}</title>
                                </Helmet>
                                <div className={`${styles.container} ${styles.fcvRow}`}>
                                    <ProfileSideBar
                                        activeTab={activeTab}
                                        setActiveTab={setActiveTab}
                                        tabs={tabs}
                                        description={employer.employer_status == 'business' ? t('businessProfileDescription') : t('individualProfileDescription')}
                                    />
                                    <div className={styles.editContainer}>
                                        {completionPercentage < 100 && <CompleteProfile darkTheme progress={completionPercentage} />}
                                        {activeTab === 0 && (
                                            <>
                                                {employer.employer_status == 'business' ?
                                                    <EmployerPersonalInformation activeTab={activeTab} employer={employer} />
                                                    :
                                                    <>
                                                        <AboutMeSection jobseeker={employer} isemployer={true} />
                                                        <EmployerPersonalInformationIndividual activeTab={activeTab} employer={employer} />
                                                    </>
                                                }
                                            </>
                                        )}
                                        {activeTab === 1 && <EmployerContactDetail activeTab={activeTab} employer={employer} />}
                                        {activeTab === 2 && user.user.employer_status === 'business' && <CompanyInformation activeTab={activeTab} employer={employer} />}
                                        {activeTab === 3 && user.user.employer_status === 'business' && <Account activeTab={activeTab} employer={employer} />}
                                        {activeTab === 2 && user.user.employer_status !== 'business' && <Account activeTab={activeTab} employer={employer} />}
                                        <p className={styles.switchAccountText}>
                                            {employer.employer_status == 'business' ?
                                                t('businessAccountMessage') + ' ' :
                                                t('individualAccountMessage') + ' '
                                            }
                                            <span
                                                style={{ color: '#0464B2', textDecorationLine: 'underline', cursor: 'pointer' }}
                                                onClick={() => setIsModalVisible(true)}
                                            >
                                                {employer.employer_status == 'business' ?
                                                    t('individualMode')
                                                    :
                                                    t('businessMode')
                                                }
                                            </span>
                                        </p>
                                    </div>
                                </div>

                                {/* Modal */}
                                {isModalVisible && (
                                    <div className={styles.modalOverlay}>
                                        <div className={styles.modalContent}>
                                            <h4>{t('Switch_Account_Mode')}</h4>
                                            {employer.employer_status == 'business' ?
                                                <p>{t('switchAccountMessage')}</p>
                                                :
                                                <p>{t('switchAccountMessagebusiness')}</p>
                                            }

                                            <button
                                                className={styles.modalCloseButton}
                                                onClick={() => setIsModalVisible(false)}
                                            >
                                                {t('Close')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <Helmet>
                                    <title>FindCV - Employer Profile</title>
                                </Helmet>
                                <div className={`${styles.container} ${styles.fcvRow}`}>
                                    <div style={{ display: 'flex', gap: '15px', flex: 1 }}>
                                        <div style={{ width: '25%' }}>
                                            <BlockLoader height={520} marginBottom={10} />
                                        </div>
                                        <div style={{ width: '75%' }}>
                                            {activeTab === 0 && <BlockLoader height={120} marginBottom={10} />}
                                            <BlockLoader height={400} marginBottom={10} />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                        <div className={styles.i1}></div>
                        <div className={styles.i2}></div>
                        <div className={styles.i3}></div>
                        <div className={styles.i4}></div>
                    </div>
                ) :
                    <>
                        {!isLoading && employer ? (
                            <EmployerProfileJobseekerView jobseeker={employer} />
                        ) : profileNotFound ? (
                            <NotFound
                                title={t('not_found.title')}
                                heading={t('not_found.profile_not_found')}
                                message={t('not_found.profile_message')}
                                showHomeButton={true}
                            />
                        ) : (
                            <div className={`${styles.container} ${styles.fcvRow}`}>
                                <div style={{ display: 'flex', gap: '15px', flex: 1 }}>
                                    <div style={{ width: '25%' }}>
                                        <BlockLoader height={520} marginBottom={10} />
                                    </div>
                                    <div style={{ width: '75%' }}>
                                        <BlockLoader height={400} marginBottom={10} />
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
            )}
        </>
    );
}

export default EmployerProfile;